import React from 'react'
import { Share2, Link as LinkIcon } from 'lucide-react'
import { Button } from '@duck/ui'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'

interface PublicAssignmentEmptyStateProps {
  /** The public assignment share token — same atoken link the row's CopyLinkButton uses. */
  linkToken: string
}

// Shown inside an expanded public assignment that nobody has joined yet. A public roster fills
// only once people open the shared link, so the empty state explains that and makes copying the
// link the single, obvious next step.
export const PublicAssignmentEmptyState: React.FC<PublicAssignmentEmptyStateProps> = ({ linkToken }) => {
  const { t } = useTar()
  const url = `${window.location.origin}/?atoken=${linkToken}`

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('common.linkCopied'))
    } catch {
      toast.error(t('common.failedToCopyLink') + '\n' + url, { duration: 15000 })
    }
  }

  return (
    <div className="flex flex-col items-center px-4 py-10 text-center lg:py-12">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 ring-1 ring-inset ring-primary/20">
        <Share2 className="h-7 w-7 text-primary" aria-hidden="true" />
      </div>
      <h4 className="mb-1.5 text-sm font-semibold text-foreground">{t('partner.noPeopleJoinedYet')}</h4>
      <p className="mb-5 max-w-sm text-xs leading-relaxed text-muted-foreground">{t('partner.publicRosterEmptyDescription')}</p>
      <Button size="sm" onClick={onCopy} className="gap-1.5">
        <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {t('partner.copyPublicLink')}
      </Button>
    </div>
  )
}
